import React, { useEffect, useMemo, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography, TextField, Grid, FormGroup, FormControlLabel, Checkbox, CircularProgress, Chip, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import _ from "lodash";
import Div from "@jumbo/shared/Div";
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { LoadingButton } from '@mui/lab';
import userServices from 'app/services/user-services';
import companyServices from 'app/services/company-services';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import PrettyPatternInput from 'app/widgets/Shared/PrettyPatternInput';
import ConfirmDialog from 'app/shared/widgets/ConfirmDialog';
import DriverCostFields from 'app/pages/users/DriverCostFields';
import useJumboAuth from "@jumbo/hooks/useJumboAuth";

// El 56 de Chile va fijo en la mascara: el usuario escribe solo los 9 digitos y se guarda
// con el prefijo (11 digitos), que es lo que espera el backend.
const PREFIJO_TELEFONO = '56';
const FORMATO_TELEFONO = '56 # #### ####';

// Se muestra sin el 56, porque ya lo pone la mascara. Se quita siempre que este, tambien a
// medio escribir: mientras se tipea el estado guarda "56" + lo escrito, y si solo se quitara
// con los 11 digitos el 56 se volveria a sumar en cada tecla. Un numero viejo sin prefijo
// empieza con 9 (celular) o con el codigo de area, nunca con 56.
const telefonoSinPrefijo = (phone) => {
    const digitos = `${phone || ''}`.replace(/\D/g, '');
    return digitos.startsWith(PREFIJO_TELEFONO) ? digitos.slice(2) : digitos;
};

const telefonoConPrefijo = (digitos) => (digitos ? `${PREFIJO_TELEFONO}${digitos}` : '');

// Un numero viejo guardado sin el 56 se completa, para que no quede mostrado entero pero
// bloqueando el Guardar.
const telefonoNormalizado = (phone) => telefonoConPrefijo(telefonoSinPrefijo(phone));

// Vacio vale (los dos telefonos son opcionales); a medias no.
const telefonoValido = (phone) => !phone || /^56[0-9]{9}$/.test(telefonoNormalizado(phone));

const UserEditModal = props => {
    const {userID, initialProfileCompanyId = null, isOpen, onClose=()=>{}, onUserChange=()=>{}, onDriverLimitReached=()=>{}, allCompanies = []} = props;
    const { authUser } = useJumboAuth();

    const [currentUser, setCurrentUser] = useState(null)
    const [editOptions, setEditOptions] = useState(null)
    const [saving, setSaving] = useState(false)
    const [toggleActivationLoading, setToggleActivationLoading] = useState(false)
    const [selectedProfileCompanyId, setSelectedProfileCompanyId] = useState(null)
    const [companiesCatalog, setCompaniesCatalog] = useState([])
    const [assigningCompanyId, setAssigningCompanyId] = useState('')
    const [companyActionLoading, setCompanyActionLoading] = useState(false)
    const [confirmUnassign, setConfirmUnassign] = useState({ open: false, company: null })
    // El sueldo y la jornada del conductor viven en el perfil de la compania que se esta
    // editando, no en el usuario: el mismo chofer puede manejar para dos empresas y cobrar
    // distinto en cada una. Por eso se recargan al cambiar de compania.
    const [driverSettings, setDriverSettings] = useState({})
    const isSuperadmin = _.some(authUser?.profiles, (profile) => profile.name === 'superadmin');
    const canManageUsers = _.some(authUser?.profiles, (profile) => (
        ['superadmin', 'customer_admin', 'supervisor'].includes(profile.name)
    ));
    const userCompanies = useMemo(() => _.uniqBy(currentUser?.companies || [], 'id'), [currentUser]);
    const profileCompanyIds = useMemo(() => _.chain(currentUser?.profiles || [])
        .map((profile) => profile?.company_id)
        .filter((companyId) => companyId !== null && companyId !== undefined && companyId !== '')
        .uniq()
        .value(), [currentUser]);
    const editableCompanyId = selectedProfileCompanyId;
    const selectedProfileCompany = _.find(userCompanies, (company) => `${company.id}` === `${editableCompanyId}`);
    const isEditableCompanyProfile = (profile) => `${profile.company_id}` === `${editableCompanyId}`;
    const editableCompanyProfiles = _.filter(currentUser?.profiles || [], isEditableCompanyProfile);
    const esConductor = _.some(editableCompanyProfiles, (profile) => profile.name === 'driver');
    const perfilConductor = _.find(editableCompanyProfiles, (profile) => profile.name === 'driver');
    const currentProfileNames = _.uniq(_.map(editableCompanyProfiles, 'name'));
    const telefonosValidos = telefonoValido(currentUser?.phone_primary) && telefonoValido(currentUser?.phone_secondary);
    const canSave = Boolean(canManageUsers && currentUser && editableCompanyId && editableCompanyProfiles.length > 0 && telefonosValidos);
    const effectiveCompaniesCatalog = (allCompanies && allCompanies.length > 0) ? allCompanies : companiesCatalog;
    const assignableCompanies = _.differenceBy(effectiveCompaniesCatalog, userCompanies, (company) => `${company.id}`);

    useEffect(() => {
        if(!userID) return

        setCurrentUser(null)
        // al abrir otro usuario la compania elegida antes ya no aplica
        setSelectedProfileCompanyId(null)
        userServices.getUser(userID, '?include=profiles,companies')
            .then(resp => {
                setCurrentUser(resp)
            })
            .catch(function (error) {});
    }, [userID])

    useEffect(() => {
        if (!currentUser) return

        const userCompanyIds = _.map(userCompanies, (company) => `${company.id}`);
        // Respeta lo que eligio el usuario en el selector: solo se recalcula al abrir
        // el modal o cuando la compania seleccionada dejo de estar asignada.
        if (selectedProfileCompanyId && userCompanyIds.includes(`${selectedProfileCompanyId}`)) return

        const preferredCompanyId = [
            initialProfileCompanyId,
            currentUser?.current_company_id,
            profileCompanyIds[0],
            userCompanies[0]?.id,
            authUser?.current_company_id
        ]
            .find((companyId) => companyId && userCompanyIds.includes(`${companyId}`));

        setSelectedProfileCompanyId(preferredCompanyId || null)
    }, [authUser?.current_company_id, currentUser, initialProfileCompanyId, profileCompanyIds, selectedProfileCompanyId, userCompanies])

    useEffect(() => {
        if(!canManageUsers || editOptions) return

        userServices.getEditOptions().then(resp => { setEditOptions(resp) });
    }, [canManageUsers, editOptions])

    // Al cargar el usuario o al cambiar de compania, los campos de costeo se llenan con lo que
    // tenga el perfil de conductor de ESA compania.
    useEffect(() => {
        setDriverSettings(perfilConductor?.routing_settings || {})
    }, [perfilConductor])

    useEffect(() => {
        if(!isSuperadmin || (allCompanies && allCompanies.length > 0) || companiesCatalog.length > 0) return

        companyServices.getAllCompanies()
            .then(resp => { setCompaniesCatalog(Array.isArray(resp) ? resp : (resp?.data || [])) })
            .catch(function (error) {});
    }, [isSuperadmin, allCompanies, companiesCatalog.length])

    const saveUser = () => {
        setSaving(true)
        const payload = {
            first_name: currentUser.first_name,
            last_name: currentUser.last_name,
            email: currentUser.email,
            phone_primary: telefonoNormalizado(currentUser.phone_primary),
            phone_secondary: telefonoNormalizado(currentUser.phone_secondary),
            activated: currentUser.activated,
            profile_company_id: editableCompanyId,
            profiles: _.map(editableCompanyProfiles, (profile) => ({
                company_id: editableCompanyId,
                name: profile.name
            })),
            routing_settings: driverSettings || {}
        };

        if(isSuperadmin) {
            payload.current_company_id = currentUser.current_company_id;
        }

        userServices.saveUser(currentUser.id, "?include=companies,profiles", { user: payload })
            .then(resp => {
                onUserChange(resp)
                onClose()
                setSaving(false)
            })
            .catch(function (error) {
                setSaving(false)
                const data = error?.response?.data;
                if (data?.error_code === 'subscription_limit_reached' && data?.feature === 'usuariosMobile') {
                    onDriverLimitReached(data);
                }
            });
    }

    const toggleActivation = () => {
        if(!currentUser?.id) return

        setToggleActivationLoading(true)
        userServices.toggleUserActivation(currentUser.id, "?include=companies,profiles")
            .then(resp => {
                setCurrentUser(resp)
                onUserChange(resp)
            })
            .catch(function (error) {})
            .finally(() => {
                setToggleActivationLoading(false)
            });
    }

    const handleAssignCompany = () => {
        if(!assigningCompanyId || !currentUser?.id) return

        setCompanyActionLoading(true)
        userServices.assignCompany(currentUser.id, assigningCompanyId, "?include=companies,profiles")
            .then(resp => {
                setCurrentUser(resp)
                onUserChange(resp)
                setAssigningCompanyId('')
            })
            .catch(function (error) {})
            .finally(() => { setCompanyActionLoading(false) });
    }

    const handleUnassignCompany = () => {
        const companyId = confirmUnassign.company?.id;
        if(!currentUser?.id || !companyId) return

        setCompanyActionLoading(true)
        userServices.unassignCompany(currentUser.id, companyId, "?include=companies,profiles")
            .then(resp => {
                setCurrentUser(resp)
                onUserChange(resp)
                setConfirmUnassign({ open: false, company: null })
            })
            .catch(function (error) {})
            .finally(() => { setCompanyActionLoading(false) });
    }

    const handleProfileSelect = (event, profile_name) => {
        if(!editableCompanyId) return

        if(event.target.checked){
            if(_.some(editableCompanyProfiles, (profile) => profile.name === profile_name)) return

            setCurrentUser({
                ...currentUser,
                profiles: _.concat(currentUser.profiles || [], {company_id: editableCompanyId, name: profile_name })
            })
        } else {
            setCurrentUser({
                ...currentUser,
                profiles: _.filter(currentUser.profiles, (profile) => {
                    return !isEditableCompanyProfile(profile) || profile.name !== profile_name;
                })
            })
        }
    }

    return (
        <React.Fragment>
            <Dialog
                fullWidth={true}
                maxWidth={"md"}
                open={isOpen}
                onClose={onClose}>
                <DialogTitle>
                    <Typography variant={"h2"} color="primary">{currentUser ? currentUser.first_name ? `${currentUser.first_name} ${currentUser.last_name || ''}` : currentUser.email : 'Cargando' }</Typography>
                    {currentUser &&
                        <Chip
                            size="small"
                            color={currentUser.activated === false ? "default" : "success"}
                            label={currentUser.activated === false ? "Inactivo" : "Activo"}
                            sx={{mt: 1}}
                        />
                    }
                </DialogTitle>
                <DialogContent>
                    { currentUser ?
                        <React.Fragment>
                            <Typography variant={"h4"} color="primary">Datos generales</Typography>
                            <Grid container spacing={3} alignItems={"baseline"} sx={{pt: 1, mb: 3}}>
                                <Grid item xs={6} lg={4}>
                                    <TextField
                                        autoFocus
                                        fullWidth
                                        label="Nombre"
                                        error={!currentUser?.first_name}
                                        value={currentUser?.first_name || ''}
                                        disabled={!canManageUsers}
                                        onChange={(e) => setCurrentUser({...currentUser, first_name: e.target.value})}
                                    />
                                </Grid>
                                <Grid item xs={6} lg={4}>
                                    <TextField
                                        fullWidth
                                        label="Apellido"
                                        error={!currentUser?.last_name}
                                        value={currentUser?.last_name || ''}
                                        disabled={!canManageUsers}
                                        onChange={(e) => setCurrentUser({...currentUser, last_name: e.target.value})}
                                    />
                                </Grid>
                                <Grid item xs={6} lg={4}>
                                    <TextField
                                        fullWidth
                                        label="Correo electrónico"
                                        error={!currentUser?.email}
                                        value={currentUser?.email || ''}
                                        disabled={!canManageUsers}
                                        onChange={(e) => setCurrentUser({...currentUser, email: e.target.value})}
                                    />
                                </Grid>
                                <Grid item xs={6} lg={4}>
                                    <PrettyPatternInput
                                        kind="phone"
                                        fullWidth
                                        label="Teléfono primario"
                                        format={FORMATO_TELEFONO}
                                        mask="_"
                                        allowEmptyFormatting
                                        valueIsNumericString
                                        helperText={telefonoValido(currentUser?.phone_primary) ? "Ej: 56 9 1234 5678" : "Faltan dígitos: son 9 después del 56"}
                                        error={!telefonoValido(currentUser?.phone_primary)}
                                        value={telefonoSinPrefijo(currentUser?.phone_primary)}
                                        disabled={!canManageUsers}
                                        onValueChange={(vals) => { setCurrentUser({...currentUser, phone_primary: telefonoConPrefijo(vals.value)}) }}
                                    />
                                </Grid>
                                <Grid item xs={6} lg={4}>
                                    <PrettyPatternInput
                                        kind="phone"
                                        fullWidth
                                        label="Teléfono secundario"
                                        format={FORMATO_TELEFONO}
                                        mask="_"
                                        allowEmptyFormatting
                                        valueIsNumericString
                                        helperText={telefonoValido(currentUser?.phone_secondary) ? "Ej: 56 9 1234 5678" : "Faltan dígitos: son 9 después del 56"}
                                        error={!telefonoValido(currentUser?.phone_secondary)}
                                        value={telefonoSinPrefijo(currentUser?.phone_secondary)}
                                        disabled={!canManageUsers}
                                        onValueChange={(vals) => { setCurrentUser({...currentUser, phone_secondary: telefonoConPrefijo(vals.value)}) }}
                                    />
                                </Grid>
                                {isSuperadmin && userCompanies.length > 0 && (
                                    <Grid item xs={12} lg={4}>
                                        <FormControl fullWidth>
                                            <InputLabel id="profiles-company-label">Compañía de perfiles</InputLabel>
                                            <Select
                                                labelId="profiles-company-label"
                                                label="Compañía de perfiles"
                                                value={editableCompanyId || ''}
                                                disabled={!canManageUsers}
                                                onChange={(e) => setSelectedProfileCompanyId(e.target.value)}
                                            >
                                                {_.map(userCompanies, (company) => (
                                                    <MenuItem key={`profiles_company_${company.id}`} value={company.id}>
                                                        {company.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                )}
                                {isSuperadmin && userCompanies.length > 0 && (
                                    <Grid item xs={12} lg={4}>
                                        <FormControl fullWidth>
                                            <InputLabel id="active-company-label">Compañía activa</InputLabel>
                                            <Select
                                                labelId="active-company-label"
                                                label="Compañía activa"
                                                value={currentUser?.current_company_id || ''}
                                                disabled={!canManageUsers}
                                                onChange={(e) => setCurrentUser({...currentUser, current_company_id: e.target.value})}
                                            >
                                                {_.map(userCompanies, (company) => (
                                                    <MenuItem key={company.id} value={company.id}>
                                                        {company.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                )}
                            </Grid>

                            {isSuperadmin && (
                                <React.Fragment>
                                    <Typography variant={"h4"} color="primary">Compañías</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        Asigna o quita compañías de este usuario. Al asignar, se crea con el perfil base "Lector"; luego puedes ajustar sus perfiles más abajo.
                                    </Typography>
                                    <Div sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
                                        {userCompanies.length > 0 ? (
                                            _.map(userCompanies, (company) => (
                                                <Chip
                                                    key={`company_chip_${company.id}`}
                                                    label={company.name}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                    onDelete={() => setConfirmUnassign({ open: true, company })}
                                                    disabled={companyActionLoading}
                                                />
                                            ))
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                Este usuario no está asignado a ninguna compañía.
                                            </Typography>
                                        )}
                                    </Div>
                                    <Grid container spacing={2} alignItems={"center"} sx={{ pt: 1, mb: 3 }}>
                                        <Grid item xs={12} lg={5}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel id="assign-company-label">Asignar compañía</InputLabel>
                                                <Select
                                                    labelId="assign-company-label"
                                                    label="Asignar compañía"
                                                    value={assigningCompanyId || ''}
                                                    disabled={companyActionLoading || assignableCompanies.length === 0}
                                                    onChange={(e) => setAssigningCompanyId(e.target.value)}
                                                >
                                                    {assignableCompanies.length === 0 ? (
                                                        <MenuItem value="" disabled>No hay compañías disponibles</MenuItem>
                                                    ) : (
                                                        _.map(assignableCompanies, (company) => (
                                                            <MenuItem key={`assignable_company_${company.id}`} value={company.id}>
                                                                {company.name}
                                                            </MenuItem>
                                                        ))
                                                    )}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} lg={3}>
                                            <LoadingButton
                                                loading={companyActionLoading}
                                                variant="contained"
                                                color="success"
                                                size="small"
                                                endIcon={<AddOutlinedIcon />}
                                                loadingPosition="end"
                                                disabled={!assigningCompanyId || companyActionLoading}
                                                onClick={handleAssignCompany}
                                            >
                                                Asignar
                                            </LoadingButton>
                                        </Grid>
                                    </Grid>
                                </React.Fragment>
                            )}

                            <Typography variant={"h4"} color="primary">Perfiles</Typography>
                            {selectedProfileCompany && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    {canManageUsers ? 'Editando perfiles para' : 'Perfiles asignados en'} {selectedProfileCompany.name}
                                </Typography>
                            )}
                            <Div sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
                                {currentProfileNames.length > 0 ? (
                                    _.map(currentProfileNames, (profileName) => (
                                        <Chip
                                            key={`profile_chip_${profileName}`}
                                            label={editOptions?.profile_names?.find((profile) => profile.name === profileName)?.human_name || profileName}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    ))
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        Este usuario no tiene perfiles asignados en esta compañía.
                                    </Typography>
                                )}
                            </Div>
                            <Grid container spacing={3} alignItems={"baseline"} sx={{ pt: 1, mb: 3 }}>
                                {canManageUsers && editOptions?.profile_names && (
                                    <Grid item xs={6} lg={4}>
                                        <Typography variant="overline" color="text.secondary">
                                            Editar perfiles
                                        </Typography>
                                        <FormGroup>
                                            {_.map(editOptions.profile_names, (p) => {
                                            const handleClick = (e) => {
                                                handleProfileSelect(e, p.name);
                                            };

                                            return (
                                                <FormControlLabel
                                                key={p.name}
                                                label={p.human_name}
                                                    control={
                                                    <Checkbox
                                                    color="success"
                                                    checked={editableCompanyProfiles.some(
                                                        (profile) => profile.name === p.name
                                                    )}
                                                    icon={<CircleOutlinedIcon />}
                                                    checkedIcon={<CheckCircleIcon />}
                                                    disabled={!editableCompanyId || !canManageUsers}
                                                    onChange={handleClick}
                                                    />
                                                }
                                                />
                                            );
                                            })}
                                        </FormGroup>
                                    </Grid>
                                )}
                            </Grid>

                            {/* Fuera de Perfiles a proposito: no es un permiso, es el dato con
                                que se costean sus viajes. Solo aparece si es conductor, que es
                                el unico caso en que el costeo lo va a mirar. */}
                            { esConductor &&
                                <DriverCostFields
                                    settings={driverSettings}
                                    disabled={!canManageUsers}
                                    onChange={setDriverSettings}
                                />
                            }
                        </React.Fragment>
                    :
                        <Div sx={{ textAlign: 'center' }}>
                            <CircularProgress />
                        </Div>
                    }
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { onClose() } }>Cerrar</Button>
                    {canManageUsers && (
                        <LoadingButton
                            loading={toggleActivationLoading}
                            variant="outlined"
                            color={currentUser?.activated === false ? "success" : "warning"}
                            size="small"
                            disabled={!currentUser}
                            onClick={toggleActivation}>
                                {currentUser?.activated === false ? 'Activar usuario' : 'Desactivar usuario'}
                        </LoadingButton>
                    )}
                    {canManageUsers && (
                        <LoadingButton
                            loading={saving}
                            variant="contained"
                            color="success"
                            size="small"
                            endIcon={<SaveOutlinedIcon/>}
                            loadingPosition="end"
                            disabled={!canSave}
                            onClick={saveUser}>
                                { saving ? 'Guardando' : 'Guardar' }
                        </LoadingButton>
                    )}
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={confirmUnassign.open}
                title="Quitar compañía"
                message={
                    <>
                        ¿Quitar la compañía <strong>{confirmUnassign.company?.name}</strong> de este usuario?
                        <br />
                        Se borran todos los perfiles que tiene en esa compañía y pierde el acceso a sus datos.
                    </>
                }
                confirmText="Quitar"
                loading={companyActionLoading}
                onClose={() => setConfirmUnassign({ open: false, company: null })}
                onConfirm={handleUnassignCompany}
            />
        </React.Fragment>
    )
}

export default UserEditModal;
